import FileInput from '../../ui/FileInput/FileInput';
import { AttachButton } from '../FeedbackStyled';
import { FilePlate } from '../../ui/FilePlate/FilePlate';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as Attach } from '../icons/attach.svg';
import { useIndexGenerator } from '../../../../hooks/useIndexer';
import { FeedbacksApi } from '../../../../api';

const FeedbackFileUploadPrompt = styled.p`
  margin: 0;
  width: 100%;
  color: var(--black-60);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
`;

const FeedbackFileUploadRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

type FeedbackFileUploadProps = {
  onListUpdate?: (FileListIds: number[]) => void;
  isErrorOrLoading?: (err: boolean) => void;
};

enum FileErrors {
  UnsupportedType = 'Расширение файла не поддерживается',
  FileTooLarge = 'Размер файла больше 10 Мб',
  UploadError = 'Ошибка при загрузке файла на сервер',
}

export type FileLoadingStoreType = {
  operationId: number;
  attachId: number | null;
  file: File;
  status: 'loading' | 'ready' | 'error';
  error: FileErrors | null;
  isErrored: boolean;
  isLoading: boolean;
}[];

const maxFileSizeInBytes = Math.round(1024 * 1024 * 10.1); //10.1 MiB
const maxFilesCount = 5;

export const FeedbackFileUpload = forwardRef(
  (
    { ...props }: FeedbackFileUploadProps,
    ref?: React.ForwardedRef<{ removeFilesCallback: () => void } | undefined>,
  ) => {
    const supportedFileExtensions = ['.jpeg', '.jpg', '.png', '.pdf', '.xlsx', '.xls', '.doc', '.docx'];
    const [files, setFiles] = useState<FileLoadingStoreType>([]);
    const indexGen = useIndexGenerator(1);
    const errors = useMemo(
      () =>
        Array.from(
          new Set<FileErrors>(
            files.filter((file) => file.error !== null).map((file) => file.error ?? FileErrors.UnsupportedType),
          ),
        ),
      [files],
    );

    useImperativeHandle(ref, () => ({
      removeFilesCallback() {
        handleFileRemoving(null);
      },
    }));

    const handleFileUpload = (uploadedFiles: File[]) => {
      const preparedFiles: FileLoadingStoreType = [];

      for (let i = 0; i < Math.min(uploadedFiles.length, maxFilesCount - files.length); i++) {
        const fileCandidate = uploadedFiles[i];
        const error = fileCandidate.size > maxFileSizeInBytes ? FileErrors.FileTooLarge : null;
        const fileIndex = indexGen();
        const formData = new FormData();
        formData.append('file', fileCandidate);
        if (!error) {
          FeedbacksApi.uploadFeedbackAttachment({ file: { value: fileCandidate } })
            .then((result) =>
              setFiles((files) =>
                files.map((file) =>
                  file.operationId === fileIndex
                    ? { ...file, attachId: result.id, status: 'ready', error: null, isLoading: false, isErrored: false }
                    : { ...file },
                ),
              ),
            )
            .catch(() =>
              setFiles((files) =>
                files.map((file) =>
                  file.operationId === fileIndex
                    ? { ...file, status: 'error', error: FileErrors.UploadError, isLoading: false, isErrored: true }
                    : { ...file },
                ),
              ),
            );
        }
        preparedFiles.push({
          file: fileCandidate,
          error: error,
          operationId: fileIndex,
          attachId: null,
          status: error ? 'error' : 'loading',
          isLoading: !error,
          isErrored: !!error,
        });
      }

      setFiles([...files, ...preparedFiles]);
    };

    const handleFileRemoving = (opIdForRemoving: number | null) => {
      if (opIdForRemoving) {
        setFiles((files) => files.filter((file) => file.operationId !== opIdForRemoving));
      } else {
        setFiles([]);
      }
    };

    useEffect(() => {
      const initial: { isLoadingOrError: boolean; attachIds: number[] } = { isLoadingOrError: false, attachIds: [] };

      const reduced = files.reduce(
        (prev, curr) => ({
          isLoadingOrError: prev.isLoadingOrError || !!curr.error || curr.isLoading,
          attachIds: curr.attachId ? [...prev.attachIds, curr.attachId] : [...prev.attachIds],
        }),
        initial,
      );

      props.isErrorOrLoading?.(reduced.isLoadingOrError);
      props.onListUpdate?.(reduced.attachIds);
    }, [files]);

    return (
      <FeedbackFileUploadRoot data-testid="feedback-file-upload">
        <FilePlate files={files} onRemove={handleFileRemoving} errors={errors} />
        <FileInput
          onChange={handleFileUpload}
          dropProps={{ accept: supportedFileExtensions }}
          disabled={files.length >= maxFilesCount}
          style={{ width: '100%' }}
        >
          <AttachButton type="button" size="medium" disabled={files.length >= maxFilesCount} data-testid="feedback-file-upload-attach-button">
            <Attach />
            Прикрепить файл
          </AttachButton>
        </FileInput>
        <FeedbackFileUploadPrompt>
          Вы можете загрузить не более пяти файлов с расширением .docx, .doc, .xlsx, .xls, .pdf, .png, .jpg размером до
          10 Мб каждый
        </FeedbackFileUploadPrompt>
      </FeedbackFileUploadRoot>
    );
  },
);
